import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { DatePipe } from '@angular/common';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideCheck,
  lucideRefreshCw,
  lucideSearch,
  lucideShield,
} from '@ng-icons/lucide';
import { ColumnDef } from '@3ddv/software-division-components/backoffice/table';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import { InputComponent } from '@3ddv/software-division-components/backoffice/input';
import { GsTableComponent } from '../../shared/ui/table/gs-table.component';
import { ManagePrivateSellingComponent } from '../group-links/modals/manage-private-selling/manage-private-selling.component';
import { PrivateSellingWhitelistItem } from '../group-links/models/private-selling.model';
import { PrivateSellingService } from '../group-links/services/private-selling.service';
import {
  AppDialogService,
  DialogCloseReason,
} from '../layout/dialog/app-dialog.service';
import { PaginationComponent } from '../layout/pagination/pagination.component';
import { ApiResponse } from '../models/api.model';
import { AuthStore } from '../../core/stores/auth.store';
import { DeleteErrorComponent } from './modals/delete-error/delete-error.component';
import {
  DeleteComponent,
  DeleteEventResult,
} from './modals/delete/delete.component';
import { EditBulkConfirmComponent } from './modals/edit-bulk-confirm/edit-bulk-confirm.component';
import { EditBulkComponent } from './modals/edit-bulk/edit-bulk.component';
import { EditComponent } from './modals/edit/edit.component';
import { ViewComponent } from './modals/view/view.component';
import { Client } from './models/client.model';
import { QueryParams, Event as SaleEvent } from './models/event.model';
import { BulkEditAction, EventsService } from './services/events.service';
import { buildEventColumns, EventAttachmentKind } from './events.columns';

@Component({
  selector: 'app-events',
  templateUrl: './events.component.html',
  styleUrls: ['./events.component.css'],
  imports: [
    AsyncPipe,
    ButtonComponent,
    InputComponent,
    NgIcon,
    GsTableComponent,
    PaginationComponent,
  ],
  viewProviders: [
    provideIcons({ lucideCheck, lucideRefreshCw, lucideSearch, lucideShield }),
  ],
})
export class EventsComponent implements OnInit, OnDestroy {
  protected eventsService = inject(EventsService);
  private dialogService = inject(AppDialogService);
  private activatedRoute = inject(ActivatedRoute);
  private toastr = inject(ToastrService);
  protected readonly authStore = inject(AuthStore);
  private privateSellingService = inject(PrivateSellingService);
  private datePipe = new DatePipe('en-US');

  public eventsLists: SaleEvent[];
  public totalItemsInDb: number;
  public itemsPerPage: number;
  private subscriptions: Array<Subscription>;
  public refreshing: boolean;
  private currentPage: number;
  public queryParams: QueryParams;
  public dataBuffer: any;
  public columns: ColumnDef<SaleEvent>[] = [];

  public activeSearch: boolean;
  public clientData: Client;
  public searchControl = new FormControl('', { nonNullable: true });

  constructor() {
    this.subscriptions = [];
    this.refreshing = false;
    this.queryParams = { future: true };
    this.eventsLists = [];
    this.totalItemsInDb = 0;
    this.itemsPerPage = 0;
  }

  ngOnInit(): void {
    this.columns = buildEventColumns({
      datePipe: this.datePipe,
      isSelected: event => this.isEventSelected(event),
      isAllPageSelected: () => this.isAllPageSelected(),
      isSomePageSelected: () => this.isSomePageSelected(),
      onToggleEventSelected: (event, selected) =>
        this.toggleEventSelected(event, selected),
      onToggleAllOnPageSelected: selected =>
        this.toggleAllOnPageSelected(selected),
      onOpenDetails: event => this.openModalView(event.id),
      onToggleEnabled: event => this.onEventEnabledToggle(event),
      onDelete: event => this.onEventDeleteClick(event),
      onAttachmentEdit: (event, kind) => this.openModalEdit(event, kind),
      canEdit: this.authStore.canEdit(),
    });

    const resolvedWrapper = this.activatedRoute.snapshot.data['api'];
    const resolved: ApiResponse<SaleEvent[]> = resolvedWrapper['data'];
    this.eventsLists = resolved.data;

    this.privateSellingService.getClientWithWhitelist(null).subscribe(res => {
      this.clientData = res.data;
      if (!this.privateSellingService.initialWhitelist().length) {
        const whitelistCopy = [
          ...(this.clientData.privateSellingWhitelist || []),
        ];
        this.privateSellingService.initialWhitelist.set(whitelistCopy);
      }
    });

    this.dataBuffer = resolved;
    this.totalItemsInDb = resolved.metadata.total;
    this.itemsPerPage = resolved.metadata.perPage;
    this.currentPage = 1;

    const queryparams$ = this.activatedRoute.queryParams.subscribe(params => {
      if (params['page']) {
        this.currentPage = Number(params['page']);
        const data: ApiResponse<SaleEvent[]> =
          this.activatedRoute.snapshot.data['api']['data'];

        if (
          this.currentPage &&
          data.metadata.currentPage !== this.currentPage
        ) {
          this.getAll();
        }
      }
    });

    this.subscriptions.push(queryparams$);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(o => o.unsubscribe());
  }

  public openModalView(eventId: number) {
    const { closed$ } = this.dialogService.open<ViewComponent, SaleEvent>(
      ViewComponent,
      { size: 'xl', inputs: { eventId } }
    );
    const c$ = closed$.subscribe(updatedValue => {
      if (!updatedValue) return;
      this.eventsLists = this.eventsLists.map(event =>
        event.pvEventId === updatedValue.pvEventId ? updatedValue : event
      );
    });
    this.subscriptions.push(c$);
  }
  public openModalEdit(event: SaleEvent, type: EventAttachmentKind) {
    const { closed$ } = this.dialogService.open(EditComponent, {
      size: 'xl',
      inputs: { event, type },
    });
    this.refreshListOnSuccess(closed$);
  }

  public refreshEvents() {
    this.refreshing = true;
    const r$ = this.eventsService.refresh$().subscribe(res => {
      this.getAll();
    });
    this.subscriptions.push(r$);
  }
  public toggleFuture() {
    this.queryParams.future = !this.queryParams.future;
    this.refreshing = true;
    this.getAll();
  }

  public onSearchSubmit(event: Event): void {
    event.preventDefault();
    this.searchEvent();
  }

  public searchEvent(): void {
    this.activeSearch = true;
    this.refreshing = true;
    const searchEvent$ = this.eventsService
      .searchEvent$(this.searchControl.value)
      .subscribe(
        res => {
          this.refreshing = false;
          this.eventsLists = res.data;
          this.totalItemsInDb = res.metadata.total;
          this.itemsPerPage = res.metadata.perPage;
        },
        error => {
          if (error.code === 'E_SEARCH_EVENT_BAD_VALUE') {
            this.refreshing = false;
            console.error(error.message);
          }
        }
      );

    this.subscriptions.push(searchEvent$);
  }

  public restoreTable(): void {
    this.activeSearch = false;
    this.searchControl.setValue('');
    this.getAll();
  }

  public isEventSelected(event: SaleEvent): boolean {
    return this.eventsService.getSelectedEventIds().includes(event.id);
  }

  public isAllPageSelected(): boolean {
    if (!this.eventsLists.length) {
      return false;
    }
    const selectedIds = this.eventsService.getSelectedEventIds();

    return this.eventsLists.every(event => selectedIds.includes(event.id));
  }

  public isSomePageSelected(): boolean {
    const selectedIds = this.eventsService.getSelectedEventIds();
    const selectedOnPage = this.eventsLists.filter(event =>
      selectedIds.includes(event.id)
    ).length;

    return selectedOnPage > 0 && selectedOnPage < this.eventsLists.length;
  }

  public toggleEventSelected(event: SaleEvent, selected: boolean): void {
    if (selected) {
      this.eventsService.addSelectedEvent(event.id);
    } else {
      this.eventsService.removeSelectedEvent(event.id);
    }
    this.eventsLists = [...this.eventsLists];
  }

  public toggleAllOnPageSelected(selected: boolean): void {
    if (selected) {
      this.eventsService.selectAllEvents(this.eventsLists);
    } else {
      this.eventsService.unSelectAllEvents();
    }
    this.eventsLists = [...this.eventsLists];
  }

  public onEditBulkClick() {
    const selectedEventIds = this.eventsService.getSelectedEventIds();
    const events = this.eventsLists.filter(ev =>
      selectedEventIds.includes(ev.id)
    );
    const eventDescriptions = events.map(ev => ev.description);
    const { closed$ } = this.dialogService.open<
      EditBulkComponent,
      BulkEditAction
    >(EditBulkComponent, { size: 'md', inputs: { eventDescriptions } });
    const c$ = closed$.subscribe(action => {
      if (action) {
        this.onEditBulk(action);
      }
    });
    this.subscriptions.push(c$);
  }

  public onEditBulk = (action: BulkEditAction) => {
    const { closed$ } = this.dialogService.open(EditBulkConfirmComponent, {
      size: 'sm',
      inputs: {
        action,
        count: this.eventsService.getSelectedEventIds().length,
      },
    });
    const c$ = closed$.subscribe(reason => {
      if (reason === 'success') {
        this.manageBulk(action);
      }
    });
    this.subscriptions.push(c$);
  };

  public manageBulk = (action: BulkEditAction) => {
    if (action === 'delete') {
      this.eventsService.deleteBulk().subscribe(response => {
        const selectedEventIds = this.eventsService.getSelectedEventIds();
        this.eventsLists = this.eventsLists.filter(
          ev => !selectedEventIds.includes(ev.id)
        );
        this.eventsService.unSelectAllEvents();
        this.toastr.info(response.message);
      });
    } else {
      this.eventsService.editBulk(action).subscribe(() => {
        this.eventsService.unSelectAllEvents();
        this.toastr.info(`All events were succesfully ${action + 'd'}`);
      });
    }
  };

  public onPrivateSellingClick = () => {
    const { closed$, outputs } = this.dialogService.open(
      ManagePrivateSellingComponent,
      {
        size: 'lg',
        inputs: {
          whitelist: this.clientData.privateSellingWhitelist ?? [],
          isPrivateSellingEnabled: this.clientData.isPrivateSellingEnabled,
          clientId: this.clientData.id,
          groupLinkId: null,
        },
      }
    );

    outputs?.modifiedWhitelist.subscribe(value => {
      this.clientData.privateSellingWhitelist = [
        ...value,
      ] as PrivateSellingWhitelistItem[];
    });

    closed$.subscribe(() => {
      this.privateSellingService
        .getClientWithWhitelist(null, true)
        .subscribe(res => {
          this.clientData = res.data;
        });
    });
  };

  public onEventEnabledToggle(event: SaleEvent) {
    const eventCopy = { id: event.id, isEnabled: !event.isEnabled };

    this.eventsService.toggleEnableOnDb(eventCopy).subscribe({
      next: () => {
        this.eventsLists = this.eventsLists.map(ev =>
          ev.id === event.id ? { ...ev, isEnabled: !event.isEnabled } : ev
        );
        const enabled = !event.isEnabled;
        const action = enabled ? 'enabled' : 'disabled';
        this.toastr.info(`Event was succesfully ${action}`);
      },
      error: ({ error }) => {
        const message = error.errors[0].message ?? 'Unexpected error';
        this.toastr.error(message);
      },
    });
  }

  public onEventDeleteClick(event: SaleEvent) {
    const { closed$ } = this.dialogService.open<
      DeleteComponent,
      DeleteEventResult
    >(DeleteComponent, { size: 'sm', inputs: { data: event } });
    const c$ = closed$.subscribe(result => {
      if (result === 'success') {
        this.eventsLists = this.eventsLists.filter(ev => ev.id !== event.id);
        this.toastr.info(`Event was succesfully deleted`);
      } else if (result === 'error') {
        this.dialogService.open(DeleteErrorComponent, {
          size: 'sm',
          inputs: { data: { id: event.id } },
        });
      }
    });
    this.subscriptions.push(c$);
  }

  private refreshListOnSuccess(
    closed$: Observable<DialogCloseReason | undefined>
  ) {
    const c$ = closed$.subscribe(reason => {
      if (reason === 'success') {
        const a$ = this.eventsService
          .getAll$(this.currentPage, this.queryParams)
          .subscribe(res => {
            this.eventsLists = res.data;
            this.totalItemsInDb = res.metadata.total;
            this.itemsPerPage = res.metadata.perPage;
          });
        this.subscriptions.push(a$);
      }
    });

    this.subscriptions.push(c$);
  }

  private getAll() {
    const all$ = this.eventsService
      .getAll$(this.currentPage, this.queryParams)
      .subscribe(res => {
        this.refreshing = false;
        this.eventsLists = res.data;
        this.eventsService.unSelectAllEvents();
        this.dataBuffer = res.data;
        this.totalItemsInDb = res.metadata.total;
        this.itemsPerPage = res.metadata.perPage;
      });
    this.subscriptions.push(all$);
  }
}
